// utils/storage.js - Утилиты для работы с localStorage и данными (ИСПРАВЛЕНО)
import { API_CONSTANTS } from '../types/api';

// =============================================================================
// КОНСТАНТЫ ДЛЯ ХРАНЕНИЯ
// =============================================================================
const STORAGE_KEYS = {
  // Аутентификация
  AUTH_TOKEN: API_CONSTANTS.TOKEN_KEY,
  AUTH_TOKEN_EXPIRY: API_CONSTANTS.TOKEN_EXPIRY_KEY,
  AUTH_USER: API_CONSTANTS.USER_KEY,
  
  // Данные договора
  CONTRACT_DATA: 'contract_data',
  CONTRACT_DRAFT: 'contract_draft',
  CONTRACT_AUTOSAVE: 'contract_autosave',
  
  // Настройки приложения
  APP_SETTINGS: 'app_settings',
  LAST_ACTIVITY: 'last_activity'
};

// =============================================================================
// БАЗОВЫЕ ФУНКЦИИ ДЛЯ РАБОТЫ С LOCALSTORAGE
// =============================================================================

/**
 * Безопасное сохранение данных в localStorage с валидацией
 */
const setItem = (key, value) => {
  try {
    if (value === undefined) {
      return false;
    }

    let serializedValue;
    
    if (key === STORAGE_KEYS.AUTH_TOKEN || key === API_CONSTANTS.TOKEN_KEY) {
      serializedValue = value;
    } else if (typeof value === 'string' && !value.startsWith('{') && !value.startsWith('[')) {
      serializedValue = value;
    } else {
      serializedValue = JSON.stringify(value);
    }

    localStorage.setItem(key, serializedValue);
    
    const savedValue = localStorage.getItem(key);
    if (savedValue !== serializedValue) {
      throw new Error('Данные не сохранились корректно');
    }

    return true;
  } catch (error) {
    console.error(`Ошибка сохранения в localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Безопасное получение данных из localStorage с обработкой поврежденных данных
 * ИСПРАВЛЕНО: обрабатываем токены как простые строки
 */
const getItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    
    if (item === null) {
      return defaultValue;
    }

    if (item === 'undefined' || item === undefined) {
      localStorage.removeItem(key);
      return defaultValue;
    }

    if (key === STORAGE_KEYS.AUTH_TOKEN || key === API_CONSTANTS.TOKEN_KEY) {
      return item;
    }

    if (key === STORAGE_KEYS.AUTH_TOKEN_EXPIRY || key === STORAGE_KEYS.LAST_ACTIVITY) {
      const parsed = parseInt(item);
      return isNaN(parsed) ? defaultValue : parsed;
    }

    try {
      return JSON.parse(item);
    } catch (parseError) {
      return item;
    }
  } catch (error) {
    console.error(`Ошибка чтения из localStorage (${key}):`, error);
    return defaultValue;
  }
};

/**
 * Удаление элемента из localStorage
 */
const removeItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Ошибка удаления из localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Очистка всего localStorage (использовать осторожно!)
 */
const clearAll = () => {
  try {
    localStorage.clear();
    console.log('localStorage полностью очищен');
    return true;
  } catch (error) {
    console.error('Ошибка очистки localStorage:', error);
    return false;
  }
};

/**
 * Отладочная функция для просмотра всех данных в localStorage
 */
const debugStorage = () => {
  console.group('🔍 Отладка localStorage:');
  Object.values(STORAGE_KEYS).forEach(key => {
    const rawValue = localStorage.getItem(key);
    const parsedValue = getItem(key);
    
    console.log(`📦 ${key}:`);
    console.log('   Сырое значение:', rawValue);
    console.log('   Обработанное значение:', parsedValue);
    console.log('   Тип:', typeof parsedValue);
    
    if (rawValue === 'undefined') {
      console.warn(`   ⚠️ Найдена строка "undefined"`);
    }
    
    if (rawValue && rawValue !== 'null' && parsedValue === null) {
      console.warn(`   ⚠️ Не удалось распарсить значение`);
    }
  });
  console.groupEnd();
};

// =============================================================================
// УТИЛИТЫ ДЛЯ АУТЕНТИФИКАЦИИ
// =============================================================================

const authStorage = {
  /**
   * ИСПРАВЛЕННАЯ версия: Сохранение данных аутентификации с валидацией
   */
  setAuthData(token, user, expiresIn) {
    try {
      const expiryTime = Date.now() + (expiresIn * 1000);
      
      if (token && typeof token === 'string' && token.trim()) {
        setItem(STORAGE_KEYS.AUTH_TOKEN, token.trim());
        setItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRY, expiryTime);
      } else {
        throw new Error('Невалидный токен');
      }

      if (user && typeof user === 'object') {
        const normalizedUser = {
          id: user.id || user.Id || user.ID || user.userId || `temp_${Date.now()}`,
          email: user.email?.trim() || '',
          name: user.name?.trim() || user.email?.split('@')[0] || 'Пользователь',
          createdAt: user.createdAt || user.created_at || new Date().toISOString(),
          hasContract: Boolean(user.hasContract || user.has_contract),
          ...Object.keys(user)
            .filter(key => !['id', 'email', 'name', 'createdAt', 'hasContract'].includes(key))
            .reduce((acc, key) => ({ ...acc, [key]: user[key] }), {})
        };

        setItem(STORAGE_KEYS.AUTH_USER, normalizedUser);
      } else {
        throw new Error('Невалидные данные пользователя');
      }

      setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now());
      
    } catch (error) {
      console.error('authStorage.setAuthData: ошибка сохранения:', error);
      this.clearAuthData();
      throw error;
    }
  },

  /**
   * Получение JWT токена из localStorage
   */
  getToken() {
    return getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  /**
   * ИСПРАВЛЕННАЯ версия: Получение данных пользователя из localStorage с улучшенной валидацией
   */
  getUser() {
    const user = getItem(STORAGE_KEYS.AUTH_USER);
    
    if (!user || typeof user !== 'object') {
      return null;
    }

    if (user.email && typeof user.email === 'string' && user.email.trim()) {
      if (!user.id && !user.Id && !user.ID) {
        user.id = `temp_${btoa(user.email).slice(0, 8)}`;
        setItem(STORAGE_KEYS.AUTH_USER, user);
      }
      return user;
    }

    return null;
  },

  /**
   * Проверка валидности JWT токена по времени истечения
   */
  isTokenValid() {
    const token = this.getToken();
    const expiryTime = getItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRY);
    
    if (!token || !expiryTime) {
      return false;
    }
    
    return Date.now() < expiryTime;
  },

  /**
   * Получение времени истечения токена
   */
  getTokenExpiry() {
    return getItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRY);
  },

  /**
   * Очистка всех данных аутентификации
   */
  clearAuthData() {
    removeItem(STORAGE_KEYS.AUTH_TOKEN);
    removeItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRY);
    removeItem(STORAGE_KEYS.AUTH_USER);
  },

  /**
   * Обновление данных пользователя
   */
  updateUser(userData) {
    const currentUser = this.getUser();
    if (currentUser && typeof currentUser === 'object') {
      const updatedUser = { ...currentUser, ...userData };
      setItem(STORAGE_KEYS.AUTH_USER, updatedUser);
      return updatedUser;
    }
    return null;
  }
};

// =============================================================================
// УТИЛИТЫ ДЛЯ РАБОТЫ С ДОГОВОРАМИ
// =============================================================================

const contractStorage = {
  /**
   * Сохранение основных данных договора
   */
  setContractData(contractData) {
    setItem(STORAGE_KEYS.CONTRACT_DATA, contractData);
    setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now());
  },

  /**
   * Получение основных данных договора
   */
  getContractData() {
    return getItem(STORAGE_KEYS.CONTRACT_DATA, {});
  },

  /**
   * Сохранение черновика договора с версионированием
   */
  saveDraft(contractData) {
    const draftData = {
      data: contractData,
      timestamp: Date.now(),
      version: this.getDraftVersion() + 1
    };
    setItem(STORAGE_KEYS.CONTRACT_DRAFT, draftData);
  },

  /**
   * Получение черновика договора
   */
  getDraft() {
    return getItem(STORAGE_KEYS.CONTRACT_DRAFT);
  },

  /**
   * Получение версии черновика
   */
  getDraftVersion() {
    const draft = this.getDraft();
    return draft ? draft.version : 0;
  },

  /**
   * Очистка данных договора
   */
  clearContractData() {
    removeItem(STORAGE_KEYS.CONTRACT_DATA);
    removeItem(STORAGE_KEYS.CONTRACT_DRAFT);
    removeItem(STORAGE_KEYS.CONTRACT_AUTOSAVE);
  },

  /**
   * Автосохранение договора
   */
  autosave(contractData) {
    const autosaveData = {
      data: contractData,
      timestamp: Date.now(),
      type: 'autosave'
    };
    setItem(STORAGE_KEYS.CONTRACT_AUTOSAVE, autosaveData);
  },

  /**
   * Получение автосохраненных данных
   */
  getAutosaved() {
    return getItem(STORAGE_KEYS.CONTRACT_AUTOSAVE);
  }
};

// =============================================================================
// УТИЛИТЫ ДЛЯ НАСТРОЕК ПРИЛОЖЕНИЯ
// =============================================================================

const appStorage = {
  /**
   * Сохранение настроек приложения
   */
  setSettings(settings) {
    const currentSettings = this.getSettings();
    const mergedSettings = { ...currentSettings, ...settings };
    setItem(STORAGE_KEYS.APP_SETTINGS, mergedSettings);
  },

  /**
   * Получение настроек приложения
   */
  getSettings() {
    return getItem(STORAGE_KEYS.APP_SETTINGS, {
      theme: 'light',
      language: 'ru',
      autoSave: true,
      notifications: true
    });
  },

  /**
   * Получение конкретной настройки
   */
  getSetting(key, defaultValue = null) {
    const settings = this.getSettings();
    return settings[key] !== undefined ? settings[key] : defaultValue;
  },

  /**
   * Очистка настроек
   */
  clearSettings() {
    removeItem(STORAGE_KEYS.APP_SETTINGS);
  }
};

// =============================================================================
// ЭКСПОРТ
// =============================================================================

export {
  STORAGE_KEYS,
  authStorage,
  contractStorage,
  appStorage,
  setItem,
  getItem,
  removeItem,
  clearAll,
  debugStorage
};

export default {
  STORAGE_KEYS,
  authStorage,
  contractStorage,
  appStorage,
  setItem,
  getItem,
  removeItem,
  clearAll,
  debugStorage
};